import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api/client';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useReportExecutions } from '@/hooks/useReportExecutions';
import { useReportExecution } from '@/hooks/useReportExecution';
import { useReportTemplates } from '@/hooks/useReportTemplates';
import { useReportPreferences } from '@/hooks/useReportPreferences';
import { useReportPins } from '@/hooks/useReportPins';
import type { ReportExecutionSummary } from '@/api/reports';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, History, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ReportsTable } from '@/components/reports/ReportsTable';
import { ReportConfigPanel } from '@/components/reports/ReportConfigPanel';
import { ReportPreviewWithDisplayOptions, type ReportDisplayOptions } from '@/components/reports/ReportPreviewWithDisplayOptions';
import type { ReportTemplate } from '@/components/reports/ReportCard';
import { useReportContextStore } from '@/features/reports/reportContextStore';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { ErrorState } from '@/components/ui/error';
import { PanelLoading } from '@/components/ui/loading';
import { WorkspaceToolbar } from '@/components/layout/WorkspaceToolbar';
import { projectApi } from '@/api/projectApi';
import { usePostHog } from '@posthog/react';
import type { LocalReportExecution } from './reportsWorkspaceTypes';

export const ReportsWorkspace: React.FC = () => {
  const posthog = usePostHog();
  const { selectedProject } = useGlobalProject();
  const { theme } = useAppTheme();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    setProject,
    setExecutionId: setAppExecutionId,
    setDateRange: setAppDateRange,
    setSource,
    setTimezone,
    setLastRefreshAt,
    setStale,
    dateRange: appDateRange,
    timezone: contextTimezone,
  } = useReportContextStore();

  // Extract URL params
  const urlTemplate = searchParams.get('template');
  const urlExecution = searchParams.get('execution');

  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  /** Drawer tab: 'config' when opened from Browse, 'runs' when opened from Focus. */
  const [drawerTab, setDrawerTab] = useState<'config' | 'runs'>('config');
  const { pinnedSet, togglePin } = useReportPins();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search input (200ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch baseline for current project (for reports with needs_baseline)
  useEffect(() => {
    if (!selectedProject?.id) {
      setBaselineSnapshot(null);
      return;
    }
    const projectId = String(selectedProject.id);
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE_URL}/v1/snapshots/${projectId}/baseline`, { credentials: 'include', headers });
        const data = res.ok ? await res.json() : null;
        setBaselineSnapshot(data?.id ? { id: String(data.id) } : null);
      } catch {
        setBaselineSnapshot(null);
      }
    })();
  }, [selectedProject?.id]);

  const [generatingReport, setGeneratingReport] = useState(false);
  const [fullSchedule, setFullSchedule] = useState(true);
  const [showAnalysisSections, setShowAnalysisSections] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [groupBy, setGroupBy] = useState<string>('none');
  const [highFloatThresholdDays, setHighFloatThresholdDays] = useState<string>('');
  const [lowFloatThresholdDays, setLowFloatThresholdDays] = useState<string>('');
  const [batchReports, setBatchReports] = useState<Set<string>>(new Set());
  const [baselineSnapshot, setBaselineSnapshot] = useState<{ id: string } | null>(null);

  // Report display options (sections + header + template-specific e.g. milestone_status) – used for DCMA and future reports
  const [displayOptions, setDisplayOptions] = useState<{
    sections: Record<string, { visible: boolean; scale_percent?: number }>;
    header?: { show_time?: boolean };
    milestone_status?: { sort_by?: 'planned_finish' | 'actual_finish'; status_filter?: 'all' | 'complete' | 'in_progress' | 'not_started' };
    lookahead?: { start_date?: string; end_date?: string };
  }>(() => ({
    sections: {
      data_date: { visible: true },
      generated_date: { visible: true },
      project_name: { visible: true },
      summary_heading: { visible: true },
      calculation_and_logic: { visible: false },
      client_logo: { visible: false, scale_percent: 100 },
      severity_indicators: { visible: false },
      footer_confidential: { visible: true },
      footer_page_numbers: { visible: true },
    },
    header: { show_time: false },
  }));

  // Client logo availability for current execution's project (Focus Mode)
  const [hasClientLogo, setHasClientLogo] = useState(false);
  const [clientLogoUrl, setClientLogoUrl] = useState<string | null>(null);
  // Prefetch logo when project is selected so display options show logo state immediately
  const [prefetchedLogo, setPrefetchedLogo] = useState<{ projectId: string; hasLogo: boolean; url: string | null } | null>(null);

  // Report executions history - fetch all executions for project (no template_id filter)
  const { data: executionsData, refetch: refetchExecutions } = useReportExecutions({
    project_id: selectedProject?.id ? String(selectedProject.id) : undefined,
    page_size: 50,
  });
  
  // Current execution being polled (for progress tracking)
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const { data: executionStatus } = useReportExecution(currentExecutionId, !!currentExecutionId);
  
  // Local executions for newly generated reports (before server sync)
  const [localExecutions, setLocalExecutions] = useState<LocalReportExecution[]>([]);
  
  // Selected execution for preview (Focus Mode)
  const [selectedExecution, setSelectedExecution] = useState<ReportExecutionSummary | LocalReportExecution | null>(null);
  
  // Preview loading and error states
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Single long page: no pagination

  // Use React Query for templates
  const { data: reportTemplates = [], isLoading: loading, error: templatesError } = useReportTemplates();
  const { updatePreference } = useReportPreferences();

  // AppContext: when executions data is loaded/refetched, update lastRefreshAt and clear stale
  useEffect(() => {
    if (executionsData != null) {
      setLastRefreshAt(new Date().toISOString());
      setStale(false);
    }
  }, [executionsData, setLastRefreshAt, setStale]);

  // Debug: Log templates data (dev only)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (reportTemplates.length > 0) {
      console.log('✅ Report templates loaded:', reportTemplates.length);
    } else if (!loading) {
      console.warn('⚠️ No report templates found. Check API response.');
    }
    if (templatesError) {
      console.error('❌ Error loading templates:', templatesError);
    }
  }, [reportTemplates, loading, templatesError]);

  // Sync URL params: execution in URL when in Focus Mode
  const effectiveExecutionId =
    currentExecutionId ??
    (selectedExecution
      ? 'executionId' in selectedExecution
        ? selectedExecution.executionId
        : selectedExecution.execution_id
      : null) ??
    urlExecution;

  // Sync only execution to URL (focus mode). No template param — no popup, so keep URL as /app/reports.
  useEffect(() => {
    const params = new URLSearchParams();
    if (effectiveExecutionId) params.set('execution', effectiveExecutionId);
    setSearchParams(params, { replace: true });
  }, [effectiveExecutionId, setSearchParams]);
  
  // Load execution from URL on mount
  useEffect(() => {
    if (urlExecution && !currentExecutionId) {
      setCurrentExecutionId(urlExecution);
    }
  }, [urlExecution, currentExecutionId]);

  // Clear template from URL on mount so reports URL stays clean (no popup)
  useEffect(() => {
    if (urlTemplate) {
      const params = new URLSearchParams(searchParams);
      params.delete('template');
      setSearchParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once on mount to clear template from URL
  }, []);

  // AppContext: set source and timezone on mount
  useEffect(() => {
    setSource('reports');
    try {
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? null);
    } catch {
      setTimezone(null);
    }
  }, [setSource, setTimezone]);

  // AppContext: sync GlobalProjectContext selectedProject -> store
  useEffect(() => {
    setProject(
      selectedProject?.id ? String(selectedProject.id) : null,
      selectedProject?.name ?? selectedProject?.display_name ?? null
    );
  }, [selectedProject?.id, selectedProject?.name, selectedProject?.display_name, setProject]);

  // AppContext: hydrate local date range from store once (if store has value and local is empty)
  const hydratedDateRef = useRef(false);
  useEffect(() => {
    if (hydratedDateRef.current || !appDateRange) return;
    if (appDateRange.start || appDateRange.end) {
      setStartDate(appDateRange.start);
      setEndDate(appDateRange.end);
      hydratedDateRef.current = true;
    }
  }, [appDateRange]);

  // AppContext: sync date range (startDate/endDate) -> store
  useEffect(() => {
    if (startDate || endDate) {
      setAppDateRange({ start: startDate, end: endDate });
    } else {
      setAppDateRange(null);
    }
  }, [startDate, endDate, setAppDateRange]);

  // AppContext: sync executionId with URL and local state -> store
  useEffect(() => {
    const id =
      currentExecutionId ??
      (selectedExecution
        ? 'executionId' in selectedExecution
          ? selectedExecution.executionId
          : selectedExecution.execution_id
        : null) ??
      urlExecution;
    setAppExecutionId(id ?? null);
  }, [currentExecutionId, selectedExecution, urlExecution, setAppExecutionId]);

  // Load execution from URL on mount
  useEffect(() => {
    if (urlExecution && !currentExecutionId) {
      setCurrentExecutionId(urlExecution);
    }
  }, [urlExecution, currentExecutionId]);

  // Generate report function (shared between quick run and configure & run) – runs immediately, no confirmation modal
  const generateReport = async (templateId: string, templateName: string, showPreview = false, autoDownload = false) => {
    if (!selectedProject?.id) {
      setError('Please select a project first');
      showToast('error', 'Please select a project first');
      return;
    }

    // Validate date range
    if (!fullSchedule && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        setError('End date must be after start date');
        showToast('error', 'End date must be after start date');
        return;
      }
    }

    await executeReportGeneration(templateId, templateName, showPreview, autoDownload);
  };
  
  // Actual report generation execution
  const executeReportGeneration = async (templateId: string, templateName: string, showPreview = false, autoDownload = false) => {
    setGeneratingReport(true);
    setError(null);
    setStale(true);

    try {
      // Get template to check supported_context
      const template = reportTemplates.find(t => t.id === templateId);
      const supportsDateWindow = template?.supported_context?.date_window ?? false;

      // Build parameters object
      const reportParameters: Record<string, any> = {
        show_analysis_sections: showAnalysisSections,
      };
      if (template?.needs_baseline && baselineSnapshot?.id) {
        reportParameters.baseline_id = baselineSnapshot.id;
      }

      // Add time window if not full schedule AND template supports it
      if (!fullSchedule && supportsDateWindow) {
        if (startDate) reportParameters.start_date = startDate;
        if (endDate) reportParameters.end_date = endDate;
      } else if (!fullSchedule && !supportsDateWindow) {
        if (import.meta.env.DEV) console.warn(`Template ${templateId} does not support date window filtering, ignoring date range`);
      }

      // Add layout/grouping options
      if (groupBy && groupBy !== 'none') {
        reportParameters.group_by = groupBy;
      }

      // High Float / Low Float: always send explicit thresholds (drawer > displayOptions > default 5/5 so report shows rows)
      if (templateId === 'high-float-low-float:v1') {
        const highFromDrawer = highFloatThresholdDays != null && highFloatThresholdDays !== '' ? Number(highFloatThresholdDays) : null;
        const lowFromDrawer = lowFloatThresholdDays != null && lowFloatThresholdDays !== '' ? Number(lowFloatThresholdDays) : null;
        const highFromDisplay = (displayOptions as ReportDisplayOptions).high_float_threshold_days;
        const lowFromDisplay = (displayOptions as ReportDisplayOptions).low_float_threshold_days;
        reportParameters.high_float_threshold_days = highFromDrawer ?? (highFromDisplay != null && highFromDisplay !== '' ? Number(highFromDisplay) : 5);
        reportParameters.low_float_threshold_days = lowFromDrawer ?? (lowFromDisplay != null && lowFromDisplay !== '' ? Number(lowFromDisplay) : 5);
      }

      // High Duration Tasks: send duration threshold (days) from display options
      if (templateId === 'high-duration-tasks:v1') {
        const days = (displayOptions as ReportDisplayOptions).duration_threshold_days;
        if (days != null && days !== '') reportParameters.duration_threshold_days = Number(days);
      }

      // Display options (show/hide sections) – backend and DCMA template respect parameters.display_options
      reportParameters.display_options = displayOptions;
      // Milestone Status report: sort and status filter from display options → execution_parameters
      if (templateId === 'milestone-status:v1' && displayOptions?.milestone_status) {
        const ms = displayOptions.milestone_status;
        if (ms.sort_by) reportParameters.sort_by = ms.sort_by;
        if (ms.status_filter && ms.status_filter !== 'all') reportParameters.status_filter = ms.status_filter;
      }
      // Lookahead Schedule: date window from display options
      if ((templateId === 'lookahead:v1' || templateId?.toLowerCase().includes('lookahead')) && displayOptions?.lookahead) {
        const lh = displayOptions.lookahead;
        if (lh.start_date?.trim()) reportParameters.lookahead_start_date = lh.start_date.trim();
        if (lh.end_date?.trim()) reportParameters.lookahead_end_date = lh.end_date.trim();
      }
      // Preferred date format for report header/data date (normalised across app and reports)
      reportParameters.preferred_date_format = settings.dateFormat;

      // Resolve project's current import so the worker uses the same dataset as the rest of the app (fixes High Float etc. when project has multiple imports)
      let snapshotId: string | undefined;
      try {
        const headers = await getAuthHeaders();
        const snapRes = await fetch(`${API_BASE_URL}/v1/snapshots/${selectedProject.id}/latest`, { credentials: 'include', headers });
        if (snapRes.ok) {
          const snapData = await snapRes.json();
          if (snapData?.import_run_id) {
            snapshotId = snapData.import_run_id;
            reportParameters.version_id = snapData.import_run_id;
          }
        }
      } catch {
        // Proceed without snapshot_id; backend will use default import
      }

      const response = await api.post(`${API_BASE_URL}/v1/reports/execute`, {
        template_id: templateId,
        project_id: String(selectedProject.id),
        ...(snapshotId ? { snapshot_id: snapshotId } : {}),
        parameters: reportParameters,
        generate_pdf: true,
        force_regenerate: true, // Always run fresh so idempotent cache doesn't return stale report
      });

      if (!response?.data?.execution_id) {
        throw new Error('No execution ID returned from server');
      }

      const executionId = String(response.data.execution_id);
      const executionStatus = response.data.status || 'queued';
      
      // Show toast notification
      if (executionStatus === 'queued') {
        showToast('info', `Queued: "${templateName}" is being generated`);
      } else if (executionStatus === 'completed') {
        showToast('success', `Completed: "${templateName}" generated successfully`);
      }
      
      // Start polling for execution status
      setCurrentExecutionId(executionId);
      
      // Create local execution record
      const execution: LocalReportExecution = {
        executionId,
        templateId,
        templateName,
        projectId: String(selectedProject.id),
        projectName: selectedProject.name || 'Unknown Project',
        status: executionStatus,
        generatedAt: new Date().toISOString(),
      };

      setLocalExecutions(prev => [execution, ...prev]);
      
      posthog?.capture('report_run', {
        template_id: templateId,
        template_name: templateName,
        project_id: String(selectedProject.id),
        execution_id: executionId,
        preview: showPreview,
        auto_download: autoDownload,
      });
      
      // Preview is PDF-only; ReportPreviewWithDisplayOptions will fetch when user opens preview tab
      
      // Auto-download if requested and completed (no "Preparing PDF download..." here—only on explicit Download clicks)
      if (autoDownload && executionStatus === 'completed') {
        try {
          await downloadPDF(executionId, templateName, undefined, {
            projectName: selectedProject?.name,
            date: (execution as any)?.created_at ?? (execution as any)?.executed_at,
          });
          showToast('success', `PDF "${templateName}" downloaded successfully`);
          setError(null);
        } catch (downloadErr: any) {
          if (import.meta.env.DEV) console.error('Auto-download failed:', downloadErr);
          showToast('error', `Failed: ${downloadErr.message}`, 0);
          setError(`Report generated but download failed: ${downloadErr.message}. You can download it from the History tab.`);
        }
      }
      
      // If this is the configured report, enter Focus Mode when completed or queued
      if (selectedReport === templateId) {
        if (executionStatus === 'completed' && showPreview) {
          setSelectedExecution(execution);
        }
        if (executionStatus === 'queued' || (executionStatus === 'completed' && showPreview)) {
          setCurrentExecutionId(executionId);
        }
      } else if (autoDownload && executionStatus === 'completed') {
        setSelectedReport(templateId);
        setSelectedExecution(execution);
      }

      return execution;
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('Error generating report:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to generate report');
      throw err;
    } finally {
      setGeneratingReport(false);
    }
  };

  // Download PDF function
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  
  /** options: pass display_options, preferred_date_format, and high/low float thresholds so download returns same bytes as preview */
  const downloadPDF = useCallback(async (
    executionId: string,
    templateName: string,
    templateId?: string | null,
    options?: {
      display_options?: ReportDisplayOptions | { sections?: Record<string, { visible: boolean }>; header?: { show_time?: boolean } };
      preferred_date_format?: string;
      preferred_timezone?: string;
      high_float_threshold_days?: string | number;
      low_float_threshold_days?: string | number;
      duration_threshold_days?: string | number;
      /** Project name for download filename (project_reporttype_date.pdf) */
      projectName?: string;
      /** ISO date string for filename (e.g. executed_at); falls back to today */
      date?: string;
    }
  ): Promise<void> => {
    setDownloadingPdf(executionId);
    try {
      const params = new URLSearchParams();
      if (templateId) params.set('template_id', templateId);
      if (options?.display_options || options?.preferred_date_format || options?.preferred_timezone || options?.high_float_threshold_days != null || options?.low_float_threshold_days != null || options?.duration_threshold_days != null) {
        const optionsPayload: Record<string, unknown> = {};
        if (options?.display_options) optionsPayload.display_options = options.display_options;
        if (options?.preferred_date_format) optionsPayload.preferred_date_format = options.preferred_date_format;
        if (options?.preferred_timezone) optionsPayload.preferred_timezone = options.preferred_timezone;
        if (options?.high_float_threshold_days != null && options.high_float_threshold_days !== '') optionsPayload.high_float_threshold_days = options.high_float_threshold_days;
        if (options?.low_float_threshold_days != null && options.low_float_threshold_days !== '') optionsPayload.low_float_threshold_days = options.low_float_threshold_days;
        if (options?.duration_threshold_days != null && options.duration_threshold_days !== '') optionsPayload.duration_threshold_days = options.duration_threshold_days;
        params.set('options', JSON.stringify(optionsPayload));
      }
      const query = params.toString();
      const url = `${API_BASE_URL}/v1/reports/executions/${executionId}/pdf${query ? `?${query}` : ''}`;
      const response = await api.get(url, { responseType: 'blob', timeout: 60000 });
      if (!response?.data) throw new Error('No PDF data received');
      const blobUrl = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const sanitize = (s: string) => s.replace(/[^a-z0-9\s-]/gi, '_').replace(/\s+/g, '_').slice(0, 80) || 'report';
      let downloadName: string;
      if (options?.projectName) {
        const dateStr = options?.date
          ? new Date(options.date).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10);
        downloadName = `${sanitize(options.projectName)}_${sanitize(templateName)}_${dateStr}.pdf`;
      } else {
        downloadName = `${sanitize(templateName)}-${executionId.substring(0, 8)}.pdf`;
      }
      const contentDisposition = response.headers?.['content-disposition'];
      const serverFilename =
        contentDisposition &&
        (/(?:filename\*?=(?:UTF-8'')?["']?)([^"'\s;]+)/i.exec(contentDisposition) ||
          /filename=["']?([^"'\s;]+)["']?/i.exec(contentDisposition))?.[1];
      if (serverFilename && serverFilename.toLowerCase().endsWith('.pdf') && !serverFilename.startsWith('report-')) {
        try {
          downloadName = decodeURIComponent(serverFilename.trim());
        } catch {
          // keep downloadName as built above
        }
      }
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(blobUrl);
      showToast('success', `PDF "${templateName}" downloaded successfully`);
      posthog?.capture('report_download', {
        execution_id: executionId,
        template_id: templateId ?? undefined,
        template_name: templateName,
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        showToast('error', 'Failed: PDF not found. Generate the report again.', 0);
      } else {
        showToast('error', `Failed: ${err.message || 'PDF download failed'}`, 0);
      }
      throw err;
    } finally {
      setDownloadingPdf(null);
    }
  }, [showToast, posthog]);

  /** Enter Focus Mode and load preview for an execution. */
  const loadPreview = useCallback((execution: ReportExecutionSummary | LocalReportExecution) => {
    setPreviewError(null);
    setSelectedExecution(execution);
    const eid = 'executionId' in execution ? execution.executionId : execution.execution_id;
    setCurrentExecutionId(eid ?? null);
  }, []);
  
  // Track if we've already handled completion/failure to prevent loops
  const handledStatusRef = useRef<Set<string>>(new Set());
  
  // Effect to handle execution status updates from polling
  useEffect(() => {
    const ref = handledStatusRef;
    if (!executionStatus || !currentExecutionId) return;
    
    const statusKey = `${currentExecutionId}:${executionStatus.status}`;
    
    // Skip if we've already handled this status for this execution
    if (ref.current.has(statusKey)) {
      return;
    }
    
    // Update local execution status
    setLocalExecutions(prev => prev.map(e => 
      e.executionId === currentExecutionId
        ? { ...e, status: executionStatus.status }
        : e
    ));
    
    // Handle completed status
    if (executionStatus.status === 'completed') {
      ref.current.add(statusKey);
      const completedExecutionId = currentExecutionId; // Save before clearing
      setGeneratingReport(false);
      setCurrentExecutionId(null); // Stop polling
      posthog?.capture('report_generated', {
        execution_id: completedExecutionId,
        template_id: selectedReport ?? undefined,
        project_id: selectedProject?.id ?? undefined,
      });
      // Silent: remember display options for this template (fire-and-forget)
      if (selectedReport && displayOptions) {
        updatePreference(selectedReport, displayOptions as { sections: Record<string, { visible: boolean; scale_percent?: number }>; header?: { show_time?: boolean } }).catch(() => {});
      }
      showToast('success', 'Completed: Report generated successfully');
      
      // Refetch executions list and then load preview
      setTimeout(async () => {
        try {
          const { data: freshData } = await refetchExecutions();
          
          if (import.meta.env.DEV) {
            console.log('🔄 Refetched executions:', freshData?.executions?.length || 0);
            console.log('🔍 Looking for execution:', completedExecutionId);
            console.log('📋 Available executions:', freshData?.executions?.map((e: any) => ({
              id: e.execution_id || e.executionId,
              template: e.template_id || e.templateId,
              project: e.project_id || e.projectId,
              status: e.status
            })));
          }
          
          // Find the completed execution from the freshly refetched data
          const updatedExecutions = freshData?.executions || [];
          const completedExec = updatedExecutions.find((e: any) => {
            const execId = e.execution_id || e.executionId;
            return execId === completedExecutionId;
          });
          
          if (completedExec) {
            if (import.meta.env.DEV) console.log('✅ Found completed execution in refetched data');
            setLocalExecutions(prev => prev.filter(e => e.executionId !== completedExecutionId));
            setLastRefreshAt(new Date().toISOString());
            setStale(false);
            loadPreview(completedExec);
          } else {
            if (import.meta.env.DEV) console.warn('⚠️ Completed execution not found in refetched data');
            // If not found in refetch, try to load from executionStatus directly
            if (executionStatus?.pdf_url) {
              if (import.meta.env.DEV) console.log('📄 Using executionStatus data to load preview (PDF)');
              const mockExec: any = {
                execution_id: completedExecutionId,
                executionId: completedExecutionId,
                template_id: selectedReport,
                templateId: selectedReport,
                status: 'completed',
                artifact_pdf_url: executionStatus.pdf_url,
              };
              loadPreview(mockExec);
            } else {
              try {
                const directResponse = await api.get(`${API_BASE_URL}/v1/reports/executions/${completedExecutionId}`);
                if (directResponse.data) {
                  if (import.meta.env.DEV) console.log('✅ Loaded execution directly from API');
                  loadPreview(directResponse.data);
                }
              } catch (err) {
                if (import.meta.env.DEV) console.error('❌ Failed to load execution directly:', err);
              }
            }
          }
        } catch (err) {
          if (import.meta.env.DEV) console.error('❌ Error refetching executions:', err);
        }
      }, 2000); // Give server more time to finalize and commit to DB
    } else if (executionStatus.status === 'failed') {
      ref.current.add(statusKey);
      setGeneratingReport(false);
      setCurrentExecutionId(null);
      // Remove execution from URL immediately so "load from URL" effect doesn't restore it
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('execution');
        return next;
      }, { replace: true });
      showToast('error', `Failed: ${executionStatus.error || 'Report generation failed'}`, 0);
    }
    
    // Clean up old status keys when execution ID changes
    return () => {
      if (!currentExecutionId) {
        ref.current.clear();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- executionStatus/refetchExecutions/setSearchParams etc. intentionally limited to avoid re-run loops
  }, [executionStatus?.status, executionStatus?.error, currentExecutionId, selectedReport, displayOptions, updatePreference, refetchExecutions, showToast, loadPreview]);

  // Filter reports by search query
  const filteredReports = useMemo(() => {
    if (!debouncedSearchQuery) return reportTemplates;
    const query = debouncedSearchQuery.toLowerCase();
    return reportTemplates.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        (r.persona && r.persona.some(p => p.toLowerCase().includes(query)))
    );
  }, [reportTemplates, debouncedSearchQuery]);

  // Sort: pinned first, then by name
  const sortedReports = useMemo(() => {
    return [...filteredReports].sort((a, b) => {
      const aPinned = pinnedSet.has(a.id);
      const bPinned = pinnedSet.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [filteredReports, pinnedSet]);

  /** Select template only (row click); does not open drawer. */
  const handleSelectReport = (reportId: string) => {
    setSelectedReport(reportId);
    setError(null);
    setPreviewError(null);
  };

  /** Open History drawer for template. */
  const handleOpenHistory = (reportId: string) => {
    setSelectedReport(reportId);
    setDrawerTab('runs');
    setConfigDrawerOpen(true);
  };

  /** Run report (generate with current/default params). */
  const handleRunReport = (reportId: string) => {
    const report = reportTemplates.find(r => r.id === reportId);
    if (!report) return;
    if (report.needs_baseline && !baselineSnapshot) {
      showToast('error', 'A baseline must be uploaded and set for this project to run this report.', 0);
      return;
    }
    setSelectedReport(reportId);
    generateReport(reportId, report.name, true, false);
  };

  /** Exit Focus Mode (Back to Reports). */
  const handleBackToReports = useCallback(() => {
    setSelectedExecution(null);
    setCurrentExecutionId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('execution');
      return next;
    }, { replace: true });
    setPreviewError(null);
  }, [setSearchParams]);

  const selectedReportData = selectedReport
    ? reportTemplates.find((r) => r.id === selectedReport)
    : null;

  // Combine server executions with local executions
  const allExecutions = useMemo(() => {
    const serverExecutions: (ReportExecutionSummary | LocalReportExecution)[] = executionsData?.executions || [];
    const merged: (ReportExecutionSummary | LocalReportExecution)[] = [...serverExecutions];
    localExecutions.forEach(local => {
      if (!serverExecutions.find(s => ('execution_id' in s ? s.execution_id : s.executionId) === local.executionId)) {
        merged.push(local);
      }
    });
    return merged;
  }, [executionsData?.executions, localExecutions]);

  // Filter executions for selected template and project
  const filteredExecutions = useMemo(() => {
    if (!selectedReportData || !selectedProject?.id) return [];
    return allExecutions.filter(e => {
      const execTemplateId = 'templateId' in e ? e.templateId : ('template_id' in e ? e.template_id : null);
      const execProjectId = 'projectId' in e ? e.projectId : ('project_id' in e ? e.project_id : null);
      return execTemplateId === selectedReportData.id && 
             execProjectId === String(selectedProject.id);
    });
  }, [allExecutions, selectedReportData, selectedProject?.id]);

  // When URL has execution, hydrate selectedExecution from allExecutions when available
  useEffect(() => {
    if (!urlExecution || !allExecutions.length) return;
    const exec = allExecutions.find(
      (e) => ('executionId' in e ? e.executionId : e.execution_id) === urlExecution
    );
    if (exec) {
      setSelectedExecution(exec);
      const templateId = 'templateId' in exec ? exec.templateId : (exec as any).template_id;
      if (templateId && !selectedReport) setSelectedReport(templateId);
    }
  }, [urlExecution, allExecutions, selectedReport]);

  // Prefetch project logo when project is selected so Client Logo option is known before opening an execution
  useEffect(() => {
    if (!selectedProject?.id) {
      setPrefetchedLogo(null);
      return;
    }
    const projectId = String(selectedProject.id);
    let cancelled = false;
    projectApi.getProjectLogo(projectId).then((result) => {
      if (cancelled) return;
      setPrefetchedLogo({
        projectId,
        hasLogo: !!result,
        url: result?.url ?? null,
      });
    });
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  // Use logo for display options when in Focus Mode (execution view); prefer prefetched if same project
  useEffect(() => {
    if (!selectedExecution) {
      setHasClientLogo(false);
      setClientLogoUrl(null);
      return;
    }
    const projectId = 'projectId' in selectedExecution ? selectedExecution.projectId : (selectedExecution as any).project_id;
    const projectIdStr = projectId != null ? String(projectId) : null;
    if (!projectIdStr) {
      setHasClientLogo(false);
      setClientLogoUrl(null);
      return;
    }
    if (prefetchedLogo?.projectId === projectIdStr) {
      setHasClientLogo(prefetchedLogo.hasLogo);
      setClientLogoUrl(prefetchedLogo.url);
      return;
    }
    let cancelled = false;
    projectApi.getProjectLogo(projectIdStr).then((result) => {
      if (cancelled) return;
      if (result) {
        setHasClientLogo(true);
        setClientLogoUrl(result.url);
      } else {
        setHasClientLogo(false);
        setClientLogoUrl(null);
      }
    });
    return () => { cancelled = true; };
  }, [selectedExecution, prefetchedLogo]);


  // History items for config panel (DataGrid) - all filtered executions, no pagination slice
  const historyItems = useMemo(() => {
    return filteredExecutions.map((execution) => {
      const executionId = 'executionId' in execution ? execution.executionId : execution.execution_id;
      const templateName = 'templateName' in execution ? execution.templateName : execution.template_name;
      const generatedAt = 'generatedAt' in execution ? execution.generatedAt : (execution.created_at || '');
      const status = 'status' in execution ? execution.status : 'pending';
      return {
        id: executionId,
        name: templateName ?? 'Report',
        date: generatedAt ? formatDateByPreference(generatedAt, settings.dateFormat, { showTime: true, timeZone: settings.timezone, use24Hour: settings.use24Hour }) : 'Unknown date',
        dateRaw: generatedAt || '',
        status,
        downloading: downloadingPdf === executionId,
      };
    });
  }, [filteredExecutions, downloadingPdf, settings.dateFormat, settings.timezone, settings.use24Hour]);

  const handleExecutionSelect = useCallback(
    (id: string) => {
      const execution = filteredExecutions.find(
        (e) => ('executionId' in e ? e.executionId : e.execution_id) === id
      );
      if (execution) loadPreview(execution);
    },
    [filteredExecutions, loadPreview]
  );

  const handleExecutionDownload = useCallback(
    async (id: string) => {
      const execution = filteredExecutions.find(
        (e) => ('executionId' in e ? e.executionId : e.execution_id) === id
      );
      if (!execution) return;
      const executionId = 'executionId' in execution ? execution.executionId : execution.execution_id;
      const templateName = 'templateName' in execution ? execution.templateName : execution.template_name;
      const projectName = 'projectName' in execution ? (execution as LocalReportExecution).projectName : selectedProject?.name;
      const execDate = 'generatedAt' in execution ? (execution as LocalReportExecution).generatedAt : (execution as any)?.executed_at ?? (execution as any)?.created_at;
      try {
        showToast('loading', 'Preparing PDF download...');
        await downloadPDF(executionId, templateName, undefined, { projectName, date: execDate });
      } catch (err: any) {
        setError(`Download failed: ${err.message}`);
      }
    },
    [filteredExecutions, downloadPDF, showToast, selectedProject?.name]
  );

  const selectedExecutionId =
    selectedExecution == null
      ? null
      : 'executionId' in selectedExecution
        ? selectedExecution.executionId
        : selectedExecution.execution_id;

  // Preview content: PDF-only via ReportPreviewWithDisplayOptions (fetches /preview as blob)
  const previewContent = useMemo(() => {
    if (!selectedExecution) return null;
    
    const executionId = 'executionId' in selectedExecution ? selectedExecution.executionId : selectedExecution.execution_id;
    const templateId = 'templateId' in selectedExecution ? selectedExecution.templateId : selectedExecution.template_id;
    
    if (previewError) {
      return (
        <ErrorState
          title="Preview failed to load"
          description={previewError}
          className="h-full flex"
        />
      );
    }
    
    if (executionId) {
      return (
        <ReportPreviewWithDisplayOptions
          apiBaseUrl={API_BASE_URL}
          executionId={executionId}
          templateId={templateId ?? null}
          displayOptions={displayOptions}
          onDisplayOptionsChange={setDisplayOptions}
          theme={theme}
          preferredDateFormat={settings.dateFormat}
          preferredTimezone={settings.timezone}
          projectId={selectedProject?.id ?? null}
          onError={setPreviewError}
          onDownload={() => {
            const name = 'templateName' in selectedExecution ? selectedExecution.templateName : selectedExecution.template_name;
            const execDate = 'generatedAt' in selectedExecution
              ? (selectedExecution as LocalReportExecution).generatedAt
              : (selectedExecution as any)?.executed_at ?? (selectedExecution as any)?.created_at;
            downloadPDF(executionId, name ?? 'report', templateId ?? null, {
              display_options: displayOptions,
              preferred_date_format: settings.dateFormat,
              preferred_timezone: settings.timezone,
              high_float_threshold_days: (displayOptions as ReportDisplayOptions).high_float_threshold_days,
              low_float_threshold_days: (displayOptions as ReportDisplayOptions).low_float_threshold_days,
              duration_threshold_days: (displayOptions as ReportDisplayOptions).duration_threshold_days,
              projectName: selectedProject?.name,
              date: execDate,
            });
          }}
        />
      );
    }
    
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- downloadPDF would change every render; selectedExecution/displayOptions are key
  }, [selectedExecution, selectedProject?.id, previewError, theme, API_BASE_URL, displayOptions]);

  const configPanelProps = selectedReportData
    ? {
        selectedReport: selectedReportData,
        selectedProject,
        drawerTab,
        onDrawerTabChange: setDrawerTab,
        onClose: () => setConfigDrawerOpen(false),
        fullSchedule,
        onFullScheduleChange: setFullSchedule,
        startDate,
        onStartDateChange: setStartDate,
        endDate,
        onEndDateChange: setEndDate,
        highFloatThresholdDays,
        onHighFloatThresholdDaysChange: setHighFloatThresholdDays,
        lowFloatThresholdDays,
        onLowFloatThresholdDaysChange: setLowFloatThresholdDays,
        groupBy,
        onGroupByChange: setGroupBy,
        showAnalysisSections,
        onShowAnalysisSectionsChange: setShowAnalysisSections,
        onGenerate: () => generateReport(selectedReportData.id, selectedReportData.name, true, false),
        onDownload: () => generateReport(selectedReportData.id, selectedReportData.name, false, true),
        generatingReport: generatingReport || executionStatus?.status === 'running',
        error,
        onErrorRetry: async () => {
          if (currentExecutionId) {
            try {
              await api.post(`${API_BASE_URL}/v1/reports/executions/${currentExecutionId}/retry`);
              setCurrentExecutionId(null);
              setError(null);
              await generateReport(selectedReportData.id, selectedReportData.name, true, false);
            } catch (err: any) {
              setError(`Retry failed: ${err.response?.data?.detail || err.message}`);
            }
          } else {
            setError(null);
            await generateReport(selectedReportData.id, selectedReportData.name, true, false);
          }
        },
        onErrorReset: () => {
          setFullSchedule(true);
          setShowAnalysisSections(true);
          setDisplayOptions({
            sections: {
              data_date: { visible: true },
              generated_date: { visible: true },
              project_name: { visible: true },
              summary_heading: { visible: true },
              calculation_and_logic: { visible: false },
              client_logo: { visible: false, scale_percent: 100 },
              severity_indicators: { visible: false },
              footer_confidential: { visible: true },
              footer_page_numbers: { visible: true },
            },
            header: { show_time: false },
          });
          setStartDate('');
          setEndDate('');
          setHighFloatThresholdDays('');
          setLowFloatThresholdDays('');
          setGroupBy('none');
          setError(null);
        },
        onErrorDownloadLastGood: async () => {
          const lastGood = filteredExecutions.find(e =>
            'executionId' in e ? e.status === 'completed' : e.status === 'completed' && !!('artifact_pdf_url' in e ? e.artifact_pdf_url : null)
          );
          if (lastGood) {
            const execId = 'executionId' in lastGood ? lastGood.executionId : lastGood.execution_id;
            const execName = 'templateName' in lastGood ? lastGood.templateName : lastGood.template_name;
            const projectName = 'projectName' in lastGood ? (lastGood as LocalReportExecution).projectName : selectedProject?.name;
            const execDate = 'generatedAt' in lastGood ? (lastGood as LocalReportExecution).generatedAt : (lastGood as any)?.executed_at ?? (lastGood as any)?.created_at;
            try {
              showToast('loading', 'Preparing PDF download...');
              await downloadPDF(execId, execName, undefined, { projectName, date: execDate });
              setError(null);
            } catch (err: any) {
              setError(`Download failed: ${err.message}`);
            }
          } else {
            setError('No previous successful execution found');
          }
        },
        lastGoodPdfAvailable: filteredExecutions.some(e =>
          'executionId' in e ? e.status === 'completed' : e.status === 'completed' && !!('artifact_pdf_url' in e ? e.artifact_pdf_url : null)
        ),
        currentExecutionId: currentExecutionId || undefined,
        historyItems,
        selectedExecutionId,
        onExecutionSelect: handleExecutionSelect,
        onExecutionDownload: handleExecutionDownload,
      }
    : null;

  const mode: 'browse' | 'focus' = effectiveExecutionId ? 'focus' : 'browse';
  const showFullWidthPreview = mode === 'focus' && selectedReportData;

  return (
    <WorkspaceLayout
      className="flex h-full w-full min-h-0 bg-background"
    >
      <div className="flex flex-1 flex-col min-h-0 min-w-0" data-testid="reports-workspace">
        <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
          {executionStatus?.status === 'queued' && 'Report queued for generation'}
          {executionStatus?.status === 'running' && executionStatus.progress !== undefined &&
            `Report generation ${executionStatus.progress}% complete, ${executionStatus.stage || 'processing'}`}
          {executionStatus?.status === 'completed' && 'Report generation completed. Preview updated.'}
          {executionStatus?.status === 'failed' && `Report generation failed: ${executionStatus.error || 'Unknown error'}`}
          {selectedExecution && !executionStatus && 'Preview loaded'}
        </div>

        {mode === 'browse' && (
          <WorkspaceToolbar
            title=""
            leftSlot={
              <div className="relative w-full max-w-sm">
                <Input
                  type="search"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-9 pr-4 text-sm bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
                  aria-label="Search reports"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            }
            rightSlot={
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-muted-foreground hover:text-foreground border-border"
                  onClick={() => navigate('/app/reports/batch')}
                >
                  Batch Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-muted-foreground hover:text-foreground border-border"
                  onClick={() => navigate('/app/reports/methodology')}
                >
                  Methodology
                </Button>
              </div>
            }
          />
        )}

        {showFullWidthPreview ? (
          /* Focus Mode: same content padding as Dashboard/Projects; two-column layout inside */
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            <WorkspaceToolbar
              title={selectedReportData!.name}
              leftSlot={
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-muted-foreground hover:text-foreground hover:bg-muted"
                    onClick={handleBackToReports}
                  >
                    <X size={18} className="mr-2" />
                    Back to Reports
                  </Button>
                  {selectedExecution && (
                    <Badge
                      variant={
                        (selectedExecution as any).status === 'completed'
                          ? 'default'
                          : (selectedExecution as any).status === 'failed'
                            ? 'destructive'
                            : 'secondary'
                      }
                      className="text-xs font-normal"
                    >
                      {((selectedExecution as any).status ?? '—').toString().charAt(0).toUpperCase() +
                        ((selectedExecution as any).status ?? '').toString().slice(1).toLowerCase()}
                    </Badge>
                  )}
                </div>
              }
              rightSlot={
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 border-border text-foreground hover:bg-muted"
                    onClick={() => {
                      setDrawerTab('runs');
                      setConfigDrawerOpen(true);
                    }}
                    aria-label="History"
                  >
                    <History size={16} className="mr-2" />
                    History
                  </Button>
                </div>
              }
            />
            {/* Same padding as Dashboard (p-4 lg:p-6); no extra card wrapper */}
            <div className="flex-1 min-h-0 overflow-hidden p-4 lg:p-6">
              {previewLoading ? (
                <PanelLoading
                  message={generatingReport ? 'Generating report...' : 'Loading preview...'}
                  className="h-full min-h-0 flex items-center justify-center"
                />
              ) : previewError ? (
                <ErrorState
                  title="Preview failed to load"
                  description={previewError}
                  onRetry={async () => {
                    if (currentExecutionId) {
                      try {
                        await api.post(`${API_BASE_URL}/v1/reports/executions/${currentExecutionId}/retry`);
                        setCurrentExecutionId(null);
                        setError(null);
                        if (selectedReportData) await generateReport(selectedReportData.id, selectedReportData.name, true, false);
                      } catch (err: any) {
                        setError(`Retry failed: ${err.response?.data?.detail || err.message}`);
                      }
                    } else {
                      setError(null);
                      if (selectedReportData) await generateReport(selectedReportData.id, selectedReportData.name, true, false);
                    }
                  }}
                  className="h-full min-h-0 flex items-center justify-center"
                />
              ) : selectedReportData?.id === 'dcma:v1' && selectedExecution ? (
                <ReportPreviewWithDisplayOptions
                  apiBaseUrl={API_BASE_URL}
                  executionId={'executionId' in selectedExecution ? selectedExecution.executionId : selectedExecution.execution_id}
                  templateId={'templateId' in selectedExecution ? selectedExecution.templateId : selectedExecution.template_id}
                  displayOptions={displayOptions}
                  onDisplayOptionsChange={setDisplayOptions}
                  theme={theme}
                  preferredDateFormat={settings.dateFormat}
                  preferredTimezone={settings.timezone}
                  hasClientLogo={hasClientLogo}
                  clientLogoUrl={clientLogoUrl}
                  projectId={selectedProject?.id ?? null}
                  onDownload={() => {
                    const name = 'templateName' in selectedExecution ? selectedExecution.templateName : selectedExecution.template_name;
                    const execDate = 'generatedAt' in selectedExecution
                      ? (selectedExecution as LocalReportExecution).generatedAt
                      : (selectedExecution as any)?.executed_at ?? (selectedExecution as any)?.created_at;
                    downloadPDF(
                      'executionId' in selectedExecution ? selectedExecution.executionId : selectedExecution.execution_id,
                      name ?? 'report',
                      'templateId' in selectedExecution ? selectedExecution.templateId : selectedExecution.template_id,
                      {
                        display_options: displayOptions,
                        preferred_date_format: settings.dateFormat,
                        preferred_timezone: settings.timezone,
                        projectName: selectedProject?.name,
                        date: execDate,
                      }
                    );
                  }}
                  className="h-full w-full"
                />
              ) : (
                <div className="h-full w-full">{previewContent}</div>
              )}
            </div>
          </div>
        ) : (
          /* Browse Mode: single toolbar above, one long list (no pagination) */
          <ReportsTable
            reports={sortedReports}
            loading={loading}
            error={templatesError ? (templatesError as any)?.message || 'Failed to load report templates' : null}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedReport={selectedReport}
            onSelectReport={handleSelectReport}
            onRun={handleRunReport}
            onOpenHistory={handleOpenHistory}
            pinnedReportIds={pinnedSet}
            onPinToggle={togglePin}
            viewMode="list"
            totalCount={filteredReports.length}
            embedded
            baselineSnapshot={baselineSnapshot}
          />
        )}

        {/* Config / History: drawer overlay only when report selected and drawer open */}
        {configPanelProps && configDrawerOpen && (
          <ReportConfigPanel {...configPanelProps} variant="drawer" />
        )}
      </div>
    </WorkspaceLayout>
  );
};
